import { useState } from 'react'
import { trashApi, type TrashKind } from '../api/trash'
import { Button, Callout, TextField } from './ui'
import './shared.css'

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * F-095 (REQ-287-001): banner "Nel cestino dal ... - Ripristina" di una scheda anagrafica. Il pulsante
 * compare solo agli Admin (il server risponde comunque 403 agli altri).
 */
export function TrashBanner({
  kind,
  id,
  deletedAt,
  reason,
  canRestore,
  onRestored,
}: {
  kind: TrashKind
  id: number
  deletedAt: string
  reason?: string | null | undefined
  canRestore: boolean
  onRestored: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function restore() {
    setBusy(true)
    setError(null)
    try {
      await trashApi.restore(kind, id)
      onRestored()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ripristino non riuscito')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Callout
      tone="warning"
      role="alert"
      action={canRestore ? (
        <Button variant="tonal" size="sm" disabled={busy} onClick={() => void restore()}>
          {busy ? 'Ripristino...' : 'Ripristina'}
        </Button>
      ) : undefined}
    >
      <strong>Nel cestino dal {fmtDate(deletedAt)}</strong>
      {reason ? <span> - {reason}</span> : null}
      <div className="ecrm-inline-message">
        Non compare in elenchi, ricerca e duplicati. Le pratiche collegate continuano a mostrarlo.
      </div>
      {error && <div className="ecrm-inline-message ecrm-inline-message--error">{error}</div>}
    </Callout>
  )
}

/**
 * F-095: pulsante Admin "Sposta nel cestino" con motivo facoltativo (max 500 caratteri). Non cancella
 * nulla: il record si ripristina dal Cestino (Impostazioni).
 */
export function MoveToTrashButton({
  kind,
  id,
  onMoved,
}: {
  kind: TrashKind
  id: number
  onMoved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await trashApi.moveToTrash(kind, id, reason)
      setOpen(false)
      setReason('')
      onMoved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operazione non riuscita')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <Button variant="outlined" size="sm" icon="delete" onClick={() => setOpen(true)}>
        Sposta nel cestino
      </Button>
    )
  }

  return (
    <form className="ecrm-form-stack" onSubmit={e => void submit(e)}>
      <TextField
        id={`trash-reason-${kind}-${id}`}
        type="text"
        label="Motivo (facoltativo)"
        maxLength={500}
        value={reason}
        onChange={e => setReason(e.target.value)}
        supportingText="Es. inserito due volte"
        error={error ?? undefined}
      />
      <div className="ecrm-inline-actions">
        <Button variant="text" size="sm" disabled={busy} onClick={() => setOpen(false)}>
          Annulla
        </Button>
        <Button type="submit" variant="text" tone="error" size="sm" disabled={busy}>
          {busy ? 'Spostamento...' : 'Conferma'}
        </Button>
      </div>
    </form>
  )
}
